import React from 'react'
import { useParams } from 'react-router-dom'
import useAxios from 'axios-hooks'
import { FormattedMessage } from 'react-intl'

import EditEventPost from './EditEventPost'
import EditCTAPost from './EditCTAPost'
import EditOfferPost from './EditOfferPost'
import EditAlertPost from './EditAlertPost'

const ACTION_TYPE_TO_LABEL = {
  ORDER: 'Order online',
  BOOK: 'Book',
  SHOP: 'Buy',
  LEARN_MORE: 'Learn more',
  SIGN_UP: 'Sign up',
  CALL: 'Call now',
}

function createEventData(data) {
  const eventTime = data?.data?.post?.event?.schedule
  const startDate = `${eventTime?.startDate?.year}-${eventTime?.startDate?.month}-${eventTime?.startDate?.day}`
  const startTime = `T${String(eventTime?.startTime?.hours || 0).padStart(
    2,
    '0'
  )}:${String(eventTime?.startTime?.minutes || 0).padStart(2, '0')}:00.000`
  const startDatetime = new Date(`${startDate}${startTime}`).toISOString()
  const endDate = `${eventTime?.endDate?.year}-${eventTime?.endDate?.month}-${eventTime?.endDate?.day}`
  const endTime = `T${String(eventTime?.endTime?.hours || 0).padStart(
    2,
    '0'
  )}:${String(eventTime?.endTime?.minutes || 0).padStart(2, '0')}:00.000`
  const endDatetime = new Date(`${endDate}${endTime}`).toISOString()

  return {
    title: data?.data?.post?.event?.title,
    schedule: {
      startDateTime: startDatetime,
      endDateTime: endDatetime,
    },
  }
}

async function createPostForm(data) {
  const summary = data?.data?.post?.summary || ''
  const locationNames = data?.data?.locationNames || []
  const schedulePost = new Date(data?.scheduledAt)?.getTime()
  const media = await Promise.all(
    (data?.data?.post?.media || [])
      .flat(2)
      .filter(item => item)
      .map(async (item, index) => {
        const url = item?.sourceUrl || item[0]?.sourceUrl
        const extension = url.split('.').at(-1)
        const response = await fetch(url + '?x-request=xhr')
        const blob = await response.blob()
        const file = new File([blob], `image-${index}.${extension}`, {
          type: `image/${extension}`,
        })
        return Object.assign(file, {
          preview: URL.createObjectURL(file),
          id: `${index}-${new Date().getTime()}`,
        })
      })
  )
  const refCode = data?.data?.refCode
  const hasTime = data?.data?.post?.event?.schedule?.startTime?.hours

  switch (data?.type) {
    case 'Event':
      return {
        media,
        summary,
        event: createEventData(data),
        callToAction: {
          actionType: data?.data?.post?.callToAction?.actionType
            ? {
                value: data?.data?.post?.callToAction?.actionType,
                label: (
                  <FormattedMessage
                    id={
                      ACTION_TYPE_TO_LABEL[
                        data?.data?.post?.callToAction?.actionType
                      ]
                    }
                  />
                ),
              }
            : null,
          url: data?.data?.post?.callToAction?.url,
        },
        locationNames,
        schedulePost,
        hasTime,
        refCode,
      }
    case 'News':
      return {
        media,
        summary,
        callToAction: {
          actionType: data?.data?.post?.callToAction?.actionType
            ? {
                value: data?.data?.post?.callToAction?.actionType,
                label: (
                  <FormattedMessage
                    id={
                      ACTION_TYPE_TO_LABEL[
                        data?.data?.post?.callToAction?.actionType
                      ]
                    }
                  />
                ),
              }
            : null,
          url: data?.data?.post?.callToAction?.url,
        },
        locationNames,
        schedulePost,
        hasTime,
        refCode,
      }
    case 'Offer':
      return {
        media,
        summary,
        event: createEventData(data),
        offer: {
          couponCode: data?.data?.post?.offer?.couponCode,
          redeemOnlineUrl: data?.data?.post?.offer?.redeemOnlineUrl,
          termsConditions: data?.data?.post?.offer?.termsConditions,
        },
        locationNames,
        schedulePost,
        hasTime,
        refCode,
      }
    case 'Covid-19':
      return {
        summary,
        alertType: 'COVID_19',
        callToAction: {
          actionType: data?.data?.post?.callToAction?.actionType
            ? {
                value: data?.data?.post?.callToAction?.actionType,
                label: (
                  <FormattedMessage
                    id={
                      ACTION_TYPE_TO_LABEL[
                        data?.data?.post?.callToAction?.actionType
                      ]
                    }
                  />
                ),
              }
            : null,
          url: data?.data?.post?.callToAction?.url,
        },
        locationNames,
        schedulePost,
        refCode,
      }
  }
}

const EditScheduledPost = () => {
  const { scheduledPostId } = useParams()
  const [type, setType] = React.useState('')
  const [post, setPost] = React.useState({})
  const [loading, setLoading] = React.useState(true)

  const [{ data, loading: requestLoading }] = useAxios({
    method: 'GET',
    url: `/posts/scheduled/${scheduledPostId}`,
  })

  React.useEffect(() => {
    if (!data || requestLoading) return
    createPostForm(data).then(post => {
      setType(data?.type)
      setPost(post)
      setLoading(false)
    })
  }, [data, requestLoading])

  return (
    <>
      {loading ? (
        <div>Carregando...</div>
      ) : (
        <>
          {type === 'Event' && (
            <EditEventPost initialValue={post} scheduleId={scheduledPostId} />
          )}
          {type === 'News' && (
            <EditCTAPost initialValue={post} scheduleId={scheduledPostId} />
          )}
          {type === 'Offer' && (
            <EditOfferPost initialValue={post} scheduleId={scheduledPostId} />
          )}
          {type === 'Covid-19' && (
            <EditAlertPost initialValue={post} scheduleId={scheduledPostId} />
          )}
        </>
      )}
    </>
  )
}

export default EditScheduledPost
